// ============================================================================
// ACTION MODAL AUTO-SEEDING SERVICE
// ============================================================================
// Generates production-ready draft content for action modals
// Creates REAL database records (not temporary state)
// Never overwrites existing content

import { supabase } from './supabaseClient';

export interface SeedOptions {
  songId: string;
  civicLevelSlug: string;
}

export interface SeedResult {
  success: boolean;
  blocksCreated: number;
  cardsCreated: number;
  error?: string;
}

// ============================================================================
// MAIN SEEDING FUNCTION
// ============================================================================

export async function seedActionModalsForLevel(
  options: SeedOptions
): Promise<SeedResult> {
  const { songId, civicLevelSlug } = options;

  // Observer uses legacy fields - no seeding
  if (civicLevelSlug === 'observer') {
    return {
      success: false,
      blocksCreated: 0,
      cardsCreated: 0,
      error: 'Observer level uses legacy fields and cannot be seeded',
    };
  }

  try {
    // Get civic level ID
    const { data: civicLevel, error: levelError } = await supabase
      .from('civic_levels')
      .select('id')
      .eq('slug', civicLevelSlug)
      .maybeSingle();

    if (levelError || !civicLevel) {
      throw new Error(`Civic level not found: ${civicLevelSlug}`);
    }

    const civicLevelId = civicLevel.id;

    // Get song tags for context-aware seeding
    const { data: song, error: songError } = await supabase
      .from('songs')
      .select('tags, themes')
      .eq('id', songId)
      .maybeSingle();

    if (songError || !song) {
      throw new Error('Song not found');
    }

    const songTags = song.tags || [];
    const songThemes = song.themes || [];

    // Check if content already exists
    const { data: existingBlocks } = await supabase
      .from('song_modal_blocks')
      .select('id')
      .eq('song_id', songId)
      .eq('civic_level_id', civicLevelId)
      .limit(1);

    const { data: existingCards } = await supabase
      .from('song_pathway_cards')
      .select('id')
      .eq('song_id', songId)
      .eq('civic_level_id', civicLevelId)
      .limit(1);

    if (existingBlocks && existingBlocks.length > 0) {
      return {
        success: false,
        blocksCreated: 0,
        cardsCreated: 0,
        error: 'Content already exists for this level. Will not overwrite.',
      };
    }

    // Generate level-specific content
    let blocksCreated = 0;
    let cardsCreated = 0;

    switch (civicLevelSlug) {
      case 'explorer':
        blocksCreated = await seedExplorerBlocks(songId, civicLevelId, songTags);
        cardsCreated = await seedExplorerCards(songId, civicLevelId);
        break;
      case 'senseMaker':
        blocksCreated = await seedSenseMakerBlocks(songId, civicLevelId, songTags);
        cardsCreated = await seedSenseMakerCards(songId, civicLevelId);
        break;
      case 'advocate':
        blocksCreated = await seedAdvocateBlocks(songId, civicLevelId, songTags);
        cardsCreated = await seedAdvocateCards(songId, civicLevelId);
        break;
      case 'catalyst':
        blocksCreated = await seedCatalystBlocks(songId, civicLevelId, songTags);
        cardsCreated = await seedCatalystCards(songId, civicLevelId);
        break;
      case 'changeMaker':
        blocksCreated = await seedChangeMakerBlocks(songId, civicLevelId, songTags);
        cardsCreated = await seedChangeMakerCards(songId, civicLevelId);
        break;
      default:
        throw new Error(`Unknown civic level: ${civicLevelSlug}`);
    }

    return {
      success: true,
      blocksCreated,
      cardsCreated,
    };
  } catch (error: any) {
    console.error('[seedActionModalsForLevel] Error:', error);
    return {
      success: false,
      blocksCreated: 0,
      cardsCreated: 0,
      error: error.message,
    };
  }
}

// ============================================================================
// EXPLORER SEEDING
// ============================================================================

async function seedExplorerBlocks(
  songId: string,
  civicLevelId: string,
  tags: string[]
): Promise<number> {
  const themePhrase = tags.length > 0 ? `${tags[0]}` : 'this theme';

  const blocks = [
    // Look Deeper
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      modal_key: 'look_deeper',
      block_type: 'callout',
      sort_order: 0,
      status: 'draft',
      tone: 'reflective',
      block_title: 'Orientation',
      block_body: 'Start by noticing what this song brings into focus. You don\'t need a conclusion yet.',
    },
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      modal_key: 'look_deeper',
      block_type: 'prompt',
      sort_order: 1,
      status: 'draft',
      tone: 'neutral',
      block_prompt: 'Notice where this shows up around you this week. What do you see — in conversation, media, policy, or silence?',
    },
    // One Key Insight
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      modal_key: 'one_key_insight',
      block_type: 'richText',
      sort_order: 0,
      status: 'draft',
      tone: 'reflective',
      block_title: 'One Shift in Perspective',
      block_body: `This story points to how ${themePhrase} is felt before it's understood.`,
    },
    // Explore Connections prompt
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      modal_key: 'explore_connections',
      block_type: 'prompt',
      sort_order: 0,
      status: 'published', // Prompts are published by default
      tone: 'neutral',
      block_prompt: 'Pick one connection. What theme repeats?',
    },
  ];

  const { error } = await supabase.from('song_modal_blocks').insert(blocks);
  if (error) throw error;
  return blocks.length;
}

async function seedExplorerCards(
  songId: string,
  civicLevelId: string
): Promise<number> {
  const cards = [
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      sort_order: 0,
      status: 'draft',
      title: 'Carry One Question',
      description: 'Hold a single question from this story and notice where it returns this week.',
      action_type: 'observe',
      cta_label: 'Collect This Story',
    },
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      sort_order: 1,
      status: 'draft',
      title: 'Learn One Layer Deeper',
      description: 'Choose one concept or term from this story and look it up from a trusted source.',
      action_type: 'learn',
      cta_label: 'Mark As Done',
    },
  ];

  const { error } = await supabase.from('song_pathway_cards').insert(cards);
  if (error) throw error;
  return cards.length;
}

// ============================================================================
// SENSE-MAKER SEEDING
// ============================================================================

async function seedSenseMakerBlocks(
  songId: string,
  civicLevelId: string,
  tags: string[]
): Promise<number> {
  const themePhrase = tags.length > 0 ? `${tags[0]}` : 'this issue';

  const blocks = [
    // Look Deeper
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      modal_key: 'look_deeper',
      block_type: 'richText',
      sort_order: 0,
      status: 'draft',
      tone: 'neutral',
      block_title: 'Context Lens',
      block_body: 'Look for the forces around the moment: systems, incentives, and who benefits from things staying the same.',
    },
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      modal_key: 'look_deeper',
      block_type: 'prompt',
      sort_order: 1,
      status: 'draft',
      tone: 'reflective',
      block_prompt: 'Compare two contexts: your own and the one in this song. What changes? What stays the same?',
    },
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      modal_key: 'look_deeper',
      block_type: 'checklist',
      sort_order: 2,
      status: 'draft',
      tone: 'strategic',
      block_title: 'Pattern Check',
      items: [
        'What repeats across time or place?',
        'What differs depending on who holds power?',
        'What feels missing or unsaid?',
      ],
    },
    // One Key Insight
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      modal_key: 'one_key_insight',
      block_type: 'richText',
      sort_order: 0,
      status: 'draft',
      tone: 'strategic',
      block_title: 'Pattern Recognition',
      block_body: `The deeper pattern here is that ${themePhrase} often shows up as structure, not a single event.`,
    },
    // Explore Connections prompt
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      modal_key: 'explore_connections',
      block_type: 'prompt',
      sort_order: 0,
      status: 'published',
      tone: 'neutral',
      block_prompt: 'Choose two songs. What pattern repeats across time or place?',
    },
  ];

  const { error } = await supabase.from('song_modal_blocks').insert(blocks);
  if (error) throw error;
  return blocks.length;
}

async function seedSenseMakerCards(
  songId: string,
  civicLevelId: string
): Promise<number> {
  const cards = [
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      sort_order: 0,
      status: 'draft',
      title: 'Synthesize',
      description: 'Write a short analysis connecting patterns you see across contexts.',
      action_type: 'compare',
      cta_label: 'Start Writing',
    },
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      sort_order: 1,
      status: 'draft',
      title: 'Bring One Example',
      description: 'Share a real example from your context that shows this pattern.',
      action_type: 'compare',
      cta_label: 'Share Example',
    },
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      sort_order: 2,
      status: 'draft',
      title: 'Learn the History',
      description: 'Look up when and where this pattern has appeared before.',
      action_type: 'learn',
      cta_label: 'Explore History',
    },
  ];

  const { error } = await supabase.from('song_pathway_cards').insert(cards);
  if (error) throw error;
  return cards.length;
}

// ============================================================================
// ADVOCATE SEEDING
// ============================================================================

async function seedAdvocateBlocks(
  songId: string,
  civicLevelId: string,
  tags: string[]
): Promise<number> {
  const blocks = [
    // Look Deeper
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      modal_key: 'look_deeper',
      block_type: 'richText',
      sort_order: 0,
      status: 'draft',
      tone: 'strategic',
      block_title: 'What\'s at Stake',
      block_body: 'What gets protected, who gets harmed, and what becomes normal if nothing changes?',
    },
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      modal_key: 'look_deeper',
      block_type: 'prompt',
      sort_order: 1,
      status: 'draft',
      tone: 'neutral',
      block_prompt: 'Explain this to someone else in one sentence — without outrage. Just clarity.',
    },
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      modal_key: 'look_deeper',
      block_type: 'callout',
      sort_order: 2,
      status: 'draft',
      tone: 'reflective',
      block_title: 'Context-First Sharing',
      block_body: 'If you share, lead with the context, not the controversy. Let the story do the work.',
    },
    // One Key Insight
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      modal_key: 'one_key_insight',
      block_type: 'richText',
      sort_order: 0,
      status: 'draft',
      tone: 'neutral',
      block_title: 'Core Message',
      block_body: 'People need to know: this isn\'t about blame. It\'s about what happens next.',
    },
    // Explore Connections prompt
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      modal_key: 'explore_connections',
      block_type: 'prompt',
      sort_order: 0,
      status: 'published',
      tone: 'neutral',
      block_prompt: 'Find a connection that helps you explain this to someone else.',
    },
  ];

  const { error } = await supabase.from('song_modal_blocks').insert(blocks);
  if (error) throw error;
  return blocks.length;
}

async function seedAdvocateCards(
  songId: string,
  civicLevelId: string
): Promise<number> {
  const cards = [
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      sort_order: 0,
      status: 'draft',
      title: 'Draft a Clear Message',
      description: 'Write three sentences that explain what is happening and why it matters.',
      action_type: 'draft_message',
      cta_label: 'Start Writing',
    },
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      sort_order: 1,
      status: 'draft',
      title: 'Share With Context',
      description: 'Share this story with someone who needs to understand it.',
      action_type: 'share',
      cta_label: 'Prepare to Share',
    },
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      sort_order: 2,
      status: 'draft',
      title: 'Find Community Conversations',
      description: 'Look for local groups or online spaces discussing this issue.',
      action_type: 'community',
      cta_label: 'Explore Spaces',
    },
  ];

  const { error } = await supabase.from('song_pathway_cards').insert(cards);
  if (error) throw error;
  return cards.length;
}

// ============================================================================
// CATALYST SEEDING
// ============================================================================

async function seedCatalystBlocks(
  songId: string,
  civicLevelId: string,
  tags: string[]
): Promise<number> {
  const blocks = [
    // Look Deeper
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      modal_key: 'look_deeper',
      block_type: 'richText',
      sort_order: 0,
      status: 'draft',
      tone: 'strategic',
      block_title: 'Root Causes + Stakeholders',
      block_body: 'Zoom out: what institutions, policies, or incentives keep this issue in motion?',
    },
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      modal_key: 'look_deeper',
      block_type: 'checklist',
      sort_order: 1,
      status: 'draft',
      tone: 'strategic',
      block_title: 'Stakeholder Map',
      items: [
        'Who is most impacted?',
        'Who has decision power?',
        'Who benefits from the status quo?',
        'Who is already organizing or responding?',
      ],
    },
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      modal_key: 'look_deeper',
      block_type: 'prompt',
      sort_order: 2,
      status: 'draft',
      tone: 'strategic',
      block_prompt: 'Identify one leverage point: what\'s one change that would shift the system, not just the symptom?',
    },
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      modal_key: 'look_deeper',
      block_type: 'callout',
      sort_order: 3,
      status: 'draft',
      tone: 'reflective',
      block_title: 'Small Steps Are Real',
      block_body: 'Institutions exist. You don\'t have to fight them today — but you can learn where they can be moved.',
    },
    // One Key Insight
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      modal_key: 'one_key_insight',
      block_type: 'richText',
      sort_order: 0,
      status: 'draft',
      tone: 'strategic',
      block_title: 'Leverage Point',
      block_body: 'Change happens when pressure meets opportunity. Find where the system is already moving.',
    },
    // Explore Connections prompt
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      modal_key: 'explore_connections',
      block_type: 'prompt',
      sort_order: 0,
      status: 'published',
      tone: 'strategic',
      block_prompt: 'Which connection reveals stakeholders or institutions behind the issue?',
    },
  ];

  const { error } = await supabase.from('song_modal_blocks').insert(blocks);
  if (error) throw error;
  return blocks.length;
}

async function seedCatalystCards(
  songId: string,
  civicLevelId: string
): Promise<number> {
  const cards = [
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      sort_order: 0,
      status: 'draft',
      title: 'Learn from Organizers',
      description: 'Read how people are building power around this issue.',
      action_type: 'learn',
      cta_label: 'Explore Strategies',
    },
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      sort_order: 1,
      status: 'draft',
      title: 'Find Organizations',
      description: 'Identify groups working on this issue in your area.',
      action_type: 'connect',
      cta_label: 'Find Groups',
    },
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      sort_order: 2,
      status: 'draft',
      title: 'Support Their Work',
      description: 'Find ways to support organizations addressing this systemically.',
      action_type: 'support',
      cta_label: 'Learn How',
    },
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      sort_order: 3,
      status: 'draft',
      title: 'Attend an Event',
      description: 'Join a community gathering, forum, or action related to this issue.',
      action_type: 'attend',
      cta_label: 'Find Events',
    },
  ];

  const { error } = await supabase.from('song_pathway_cards').insert(cards);
  if (error) throw error;
  return cards.length;
}

// ============================================================================
// CHANGE-MAKER SEEDING
// ============================================================================

async function seedChangeMakerBlocks(
  songId: string,
  civicLevelId: string,
  tags: string[]
): Promise<number> {
  const blocks = [
    // Look Deeper
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      modal_key: 'look_deeper',
      block_type: 'richText',
      sort_order: 0,
      status: 'draft',
      tone: 'strategic',
      block_title: 'Strategy + Constraints',
      block_body: 'Real change meets constraints: time, risk, safety, resources, and backlash. Strategy is care.',
    },
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      modal_key: 'look_deeper',
      block_type: 'checklist',
      sort_order: 1,
      status: 'draft',
      tone: 'strategic',
      block_title: 'Reality Check',
      items: ['Constraints', 'Risks', 'Allies', 'Timing'],
    },
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      modal_key: 'look_deeper',
      block_type: 'prompt',
      sort_order: 2,
      status: 'draft',
      tone: 'neutral',
      block_prompt: 'Define success: what would a meaningful improvement look like — for the people most impacted?',
    },
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      modal_key: 'look_deeper',
      block_type: 'callout',
      sort_order: 3,
      status: 'draft',
      tone: 'reflective',
      block_title: 'Contribution Over Performance',
      block_body: 'Impact isn\'t a vibe. Choose a contribution that\'s sustainable and real.',
    },
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      modal_key: 'look_deeper',
      block_type: 'richText',
      sort_order: 4,
      status: 'draft',
      tone: 'neutral',
      block_title: 'Create a Usable Resource',
      block_body: 'A template, a reading list, a local map, a summary — something another person could actually use.',
    },
    // One Key Insight
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      modal_key: 'one_key_insight',
      block_type: 'richText',
      sort_order: 0,
      status: 'draft',
      tone: 'strategic',
      block_title: 'Outcomes + Accountability',
      block_body: 'Success means people most impacted see measurable improvement. Everything else is process.',
    },
    // Explore Connections prompt
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      modal_key: 'explore_connections',
      block_type: 'prompt',
      sort_order: 0,
      status: 'published',
      tone: 'strategic',
      block_prompt: 'Use connections to spot leverage: where could coordinated effort shift outcomes?',
    },
  ];

  const { error } = await supabase.from('song_modal_blocks').insert(blocks);
  if (error) throw error;
  return blocks.length;
}

async function seedChangeMakerCards(
  songId: string,
  civicLevelId: string
): Promise<number> {
  const cards = [
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      sort_order: 0,
      status: 'draft',
      title: 'Contribute Skills',
      description: 'Offer your time, skills, or resources to groups doing strategic work.',
      action_type: 'volunteer',
      cta_label: 'Find Ways to Help',
    },
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      sort_order: 1,
      status: 'draft',
      title: 'Co-create Infrastructure',
      description: 'Build campaigns, tools, or systems that help movements organize.',
      action_type: 'contribute',
      cta_label: 'Join Projects',
    },
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      sort_order: 2,
      status: 'draft',
      title: 'Strategic Support',
      description: 'Support organizations doing critical, long-term work on this issue.',
      action_type: 'support',
      cta_label: 'Find Organizations',
    },
    {
      song_id: songId,
      civic_level_id: civicLevelId,
      sort_order: 3,
      status: 'draft',
      title: 'Connect Movements',
      description: 'Help build coalitions between groups working on related issues.',
      action_type: 'connect',
      cta_label: 'Explore Coalitions',
    },
  ];

  const { error } = await supabase.from('song_pathway_cards').insert(cards);
  if (error) throw error;
  return cards.length;
}
